import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiTrendingUp, FiClock, FiAlertCircle, FiBarChart2, FiList, FiGrid, FiChevronRight } from 'react-icons/fi';
import MetaClaimsAI from '../components/common/MetaClaimsAI';
import { Plus } from 'lucide-react';
import {
  fetchWorkbenchMetrics,
  fetchCriticalTasksLane,
  fetchNeedAttentionLane,
  fetchPendingTasksLane,
  extractTaskDisplayData,
  DEFAULT_SLA_HOURS
} from '../services/manualTaskService';
import { getUserData } from '../utils/authUtils';
import {
  formatTimeRemaining,
  getTaskSeverity,
} from '../utils/taskStatistics';
import { Button } from '../styles/PageStyles';


const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #f5f7fa;
  min-height: 100vh;
  position: relative;
  
  @media (max-width: 1440px) {
    padding: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const MainContent = styled.div`
  flex: 1;
  width: 100%;
  overflow: auto;
  
  @media (max-width: 1440px) {
    margin-bottom: 1.5rem;
  }
`;

const ResizeHandle = styled.div`
  width: 8px;
  cursor: col-resize;
  background: transparent;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1002;
  transition: background 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
  
  &:active {
    background: #3b82f6;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 40px;
    background: #d1d5db;
    border-radius: 2px;
  }
  
  @media (max-width: 1440px) {
    display: none;
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(2px);
`;

const Sidebar = styled.div`
  position: fixed;
  right: ${props => props.isOpen ? '0' : `-${props.width}px`};
  top: 80px;
  bottom: 0;
  width: ${props => props.width}px;
  min-width: 500px;
  max-width: 800px;
  background: white;
  z-index: 1000;
  transition: right 0.3s ease;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  
  @media (max-width: 1440px) {
    width: 90% !important;
    min-width: unset;
    max-width: 500px;
    top: 60px;
  }
`;

const TopSection = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.5rem;
    gap: 1rem;
  }
`;

const WelcomeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SLACircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(#00A1FF ${props => props.percentage}%, #bce7ff ${props => props.percentage}%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const SLAInner = styled.div`
  width: 105px;
  height: 105px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 72px;
    height: 72px;
  }
`;

const SLALabelHeading = styled.div`
  font-size: 0.95rem;
    color: rgb(107, 114, 128);
    font-weight: 700;
    margin-bottom: 4px;
`;
const SLALabel = styled.div`
  font-size: 0.8rem;
    color: rgb(107, 114, 128);
    font-weight: 500;
    margin-top: 5px;
`;

const SLAValue = styled.div`
  font-size: 1.9rem;
  font-weight: 600;
  line-height: 1.4rem;
  color: rgb(31, 41, 55);
`;

const WelcomeContent = styled.div`
  flex: 1;
`;

const WelcomeTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const AlertBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #e8a9001a;
  color: #282828;
  padding: 0.5rem;
  border-radius: 45px;
  font-size: 0.9rem;
  font-weight: 600;
`;
const AlertCount = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: #e8a900;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;



const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1440px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const StatCard = styled.div`
  background: #f5f7fb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem 1rem 2rem;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 0.875rem;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const StatIcon = styled.div`
  color: #9ca3af;
  font-size: 1.25rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4rem 0px 0.8rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin: 1.5rem 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 500;
  color: #282828;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ViewButton = styled.button`
  background: ${props => props.active ? '#e0e7ff' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#4f46e5' : '#6b7280'};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#e0e7ff' : '#f3f4f6'};
  }
`;

const KanbanContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1440px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const ListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: calc(100vh - 400px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ListSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const ListSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
`;

const ListSectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ListTasksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const KanbanColumn = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ColumnHeader = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e3e3e3;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #282828;
  margin-bottom: 1.5rem;
  padding-left: 0.5rem;
`;

const sonarPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--dot-color), 0.5);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(var(--dot-color), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--dot-color), 0);
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  position: relative;
  --dot-color: ${props => {
    const color = props.color;
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  }};
  animation: ${sonarPulse} 2s ease-out infinite;
`;

const ColumnDescription = styled.div`
  font-size: 0.9rem;
    color: rgb(107, 114, 128);
    line-height: 1.4;
    // margin-bottom: 1rem;
    background-color: rgb(141 141 141 / 5%);
    padding: 15px;
    border-radius: 5px;
    word-spacing: 7px;
    
`;

const SortControls = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
  display:none;
`;

const SortButton = styled.button`
  background: ${props => props.active ? '#eff6ff' : '#f3f5f7'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #eff6ff;
    color: #3b82f6;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  height: calc(100vh - 400px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 0.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const TaskCard = styled.div`
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 0 1rem 0;
  border-left: 4px solid ${props => props.borderColor || '#ff3342'};
  position: relative;
  max-width: 100%;
  overflow: visible;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  // min-height: 80px;
  
  &:hover {
    border-left-color: ${props => props.borderColor || '#ff3342'};
    background-color:#f3f5f7;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:last-child{
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
  }
`;

const TaskCategory = styled.div`
  font-size: 1rem;
  color: #a4a4a4;
  margin-bottom: 0.25rem;
`;

const TaskDate = styled.div`
  font-size: 0.9rem;
  color: #9ca3af;
  text-align: right;
`;

const ClaimedAmount = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TaskHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  width: 100%;
  min-width: 0;
`;

const TaskId = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TaskAmount = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
`;

const TaskDescription = styled.div`
  font-size: .9rem;
  color: #a4a4a4;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  min-width: 0;
  overflow: hidden;
`;

const TaskDescriptionText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const CheckIcon = styled.span`
  color: #10b981;
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin: 1rem 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


const FloatingActionButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  // background: linear-gradient(135deg, #00B4D8 0%, #A855F7 100%);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 0;
  & > img {
    width: 100%;
    height: 100%;
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 1440px) {
    display: none;
  }
`;

const WorkbenchListing = () => {
  const navigate = useNavigate();
  const [sidebarWidth, setSidebarWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [activeSort, setActiveSort] = useState({
    critical: null,
    attention: null,
    pending: null
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Configurable SLA timeout in hours (change this value to adjust SLA calculation)
  const [slaTimeoutHours, setSlaTimeoutHours] = useState(DEFAULT_SLA_HOURS); // Default: 72 hours
  
  const [tasks, setTasks] = useState({ critical: [], attention: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state for each column
  const [pagination, setPagination] = useState({
    critical: { page: 1, hasMore: true, loading: false },
    attention: { page: 1, hasMore: true, loading: false },
    pending: { page: 1, hasMore: true, loading: false }
  });

  const itemsPerPage = 10;
  const criticalObserver = useRef(null);
  const attentionObserver = useRef(null);
  const pendingObserver = useRef(null);
  const [stats, setStats] = useState({
    totalCases: 0,
    avgTAT: 0,
    nearSLA: 0,
    highRisk: 0,
    slaPercentage: 0,
    yourCases: 0,
    criticalCount: 0,
    needAttentionCount: 0,
    pendingCount: 0
  });

  // IntersectionObserver callbacks for each column
  const criticalLoaderRef = useCallback(node => {
    if (!node) return;
    if (criticalObserver.current) criticalObserver.current.disconnect();

    criticalObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.critical.hasMore && !pagination.critical.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          critical: { ...prev.critical, page: prev.critical.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    criticalObserver.current.observe(node);
  }, [pagination.critical.hasMore, pagination.critical.loading, loading]);

  const attentionLoaderRef = useCallback(node => {
    if (!node) return;
    if (attentionObserver.current) attentionObserver.current.disconnect();

    attentionObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.attention.hasMore && !pagination.attention.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          attention: { ...prev.attention, page: prev.attention.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    attentionObserver.current.observe(node);
  }, [pagination.attention.hasMore, pagination.attention.loading, loading]);

  const pendingLoaderRef = useCallback(node => {
    if (!node) return;
    if (pendingObserver.current) pendingObserver.current.disconnect();

    pendingObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pagination.pending.hasMore && !pagination.pending.loading && !loading) {
        setPagination(prev => ({
          ...prev,
          pending: { ...prev.pending, page: prev.pending.page + 1, loading: true }
        }));
      }
    }, { rootMargin: '100px', threshold: 0.1 });

    pendingObserver.current.observe(node);
  }, [pagination.pending.hasMore, pagination.pending.loading, loading]);

  const buildLaneFilter = (laneKey) => {
    const sort = activeSort[laneKey];

    if (laneKey === 'critical' || laneKey === 'attention') {
      if (sort === 'High Priority') {
        return { sla_due_only: false, priority: 'high' };
      }
    }

    if (laneKey === 'pending') {
      if (sort === 'High Value') {
        return { sla_due_only: false, priority: 'high' };
      }
    }

    return { sla_due_only: false, priority: null };
  };

  // Handle resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      
      if (newWidth >= 500 && newWidth <= 800) {
        setSidebarWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Fetch metrics on initial load
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const metrics = await fetchWorkbenchMetrics();
        setStats(prev => ({
          ...prev,
          totalCases: metrics.totalCases,
          avgTAT: metrics.overallTatHours,
          nearSLA: metrics.nearSLA,
          highRisk: metrics.highRisk,
          slaPercentage: metrics.slaPercent,
        }));
      } catch (err) {
        console.error('Error fetching metrics:', err);
      }
    };
    fetchMetrics();
  }, []);

  // Fetch critical tasks with pagination
  useEffect(() => {
    const fetchCriticalTasks = async () => {
      const isInitialLoad = pagination.critical.page === 1;
      if (isInitialLoad) {
        setLoading(true);
      }

      try {
        const criticalFilter = buildLaneFilter('critical');
        const queryParams = `skip=${(pagination.critical.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const criticalLane = await fetchCriticalTasksLane(criticalFilter, queryParams);

        if (criticalLane.length === 0 || criticalLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            critical: { ...prev.critical, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            critical: { ...prev.critical, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          critical: isInitialLoad ? criticalLane : [...prev.critical, ...criticalLane]
        }));
      } catch (err) {
        console.error('Error fetching critical tasks:', err);
        setPagination(prev => ({
          ...prev,
          critical: { ...prev.critical, hasMore: false, loading: false }
        }));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    };

    fetchCriticalTasks();
  }, [pagination.critical.page, activeSort.critical]);

  // Fetch attention tasks with pagination
  useEffect(() => {
    const fetchAttentionTasks = async () => {
      const isInitialLoad = pagination.attention.page === 1;

      try {
        const attentionFilter = buildLaneFilter('attention');
        const queryParams = `skip=${(pagination.attention.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const attentionLane = await fetchNeedAttentionLane(attentionFilter, queryParams);

        if (attentionLane.length === 0 || attentionLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            attention: { ...prev.attention, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            attention: { ...prev.attention, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          attention: isInitialLoad ? attentionLane : [...prev.attention, ...attentionLane]
        }));
      } catch (err) {
        console.error('Error fetching attention tasks:', err);
        setPagination(prev => ({
          ...prev,
          attention: { ...prev.attention, hasMore: false, loading: false }
        }));
      }
    };

    fetchAttentionTasks();
  }, [pagination.attention.page, activeSort.attention]);

  // Fetch pending tasks with pagination
  useEffect(() => {
    const fetchPendingTasks = async () => {
      const isInitialLoad = pagination.pending.page === 1;

      try {
        const pendingFilter = buildLaneFilter('pending');
        const queryParams = `skip=${(pagination.pending.page - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const pendingLane = await fetchPendingTasksLane(pendingFilter, queryParams);

        if (pendingLane.length === 0 || pendingLane.length < itemsPerPage) {
          setPagination(prev => ({
            ...prev,
            pending: { ...prev.pending, hasMore: false, loading: false }
          }));
        } else {
          setPagination(prev => ({
            ...prev,
            pending: { ...prev.pending, loading: false }
          }));
        }

        setTasks(prev => ({
          ...prev,
          pending: isInitialLoad ? pendingLane : [...prev.pending, ...pendingLane]
        }));
      } catch (err) {
        console.error('Error fetching pending tasks:', err);
        setPagination(prev => ({
          ...prev,
          pending: { ...prev.pending, hasMore: false, loading: false }
        }));
      }
    };

    fetchPendingTasks();
  }, [pagination.pending.page, activeSort.pending]);

  const handleTaskClick = (task) => {
    navigate(`/workbench/${task.id}`, {
      state: {
        manualTaskId: task.id,
        // Prefer legacy workflow_code if present, otherwise fall back to pipeline_id
        workflow_code: task.workflow_code || task.pipeline_id || null,
        // Prefer legacy workflow_instance_id if present, otherwise fall back to pipeline_instance_id
        workflow_instance_id: task.workflow_instance_id || task.pipeline_instance_id || null,
        // Pass overall SLA percentage for use in detail screens
        slaPercentage: stats.slaPercentage,
      }
    });
  };

  const handleCreateClaim = () => {
    navigate(`/create-claim`)
  }

  const renderTask = (task, borderColor = '#ff3342') => {
    const displayData = extractTaskDisplayData(task);
    const timeRemainingText = formatTimeRemaining(task);
    const severity = getTaskSeverity(task);
    
    return (
      <TaskCard key={task.id} onClick={() => handleTaskClick(task)} borderColor={borderColor}>
        <TaskHeader>
           <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <TaskCategory>{displayData.case_type || displayData.category}</TaskCategory>
            <TaskDate style={{ color: severity.color }}>
              {timeRemainingText}
            </TaskDate>
          </div>
           <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <TaskId>{displayData.caseId}</TaskId>
              <ClaimedAmount>IDR {displayData.claimedAmount}</ClaimedAmount>
            </div>
        </TaskHeader>
        {displayData.case_status_summary && (
        <TaskDescription>
          <CheckIcon style={{ color: severity.color }}>✓</CheckIcon>
          <TaskDescriptionText title={displayData.case_status_summary}>
            {displayData.case_status_summary}
          </TaskDescriptionText>
        </TaskDescription>
        )}
      </TaskCard>
    );
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };


  const toggleSidebar = () => {
    setIsAISidebarOpen(!isAISidebarOpen);
  };

  return (
    <Container data-container="workbench">
      <MainContent>
        <TopSection>
          <WelcomeCard>
            <SLACircle percentage={stats.slaPercentage}>
              <SLAInner>
                <SLALabelHeading>SLA</SLALabelHeading>
                <SLAValue>{stats.slaPercentage}%</SLAValue>
                <SLALabel>On Track</SLALabel>
              </SLAInner>
            </SLACircle>
            <WelcomeContent>
              <WelcomeTitle>Welcome, {getUserData()?.firstname || 'User'} !</WelcomeTitle>
              <AlertBadge>
                <AlertCount>{stats.nearSLA}</AlertCount>
                Claims nearing SLA in 24 hrs!
              </AlertBadge>
            </WelcomeContent>
          </WelcomeCard>

          <StatsGrid>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.totalCases}</StatValue>
                  <StatLabel>Your Cases</StatLabel>
                </div>
                <StatIcon><FiTrendingUp /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>
                    {stats.avgTAT && !isNaN(stats.avgTAT) 
                      ? (stats.avgTAT < 0.01 ? '<0.1 sec' : Number(stats.avgTAT).toFixed(2) + ' hrs')
                      : '0.00 secs'
                    } <span style={{ fontSize: '1rem' }}></span>
                  </StatValue>
                  <StatLabel>Overall TAT</StatLabel>
                </div>
                <StatIcon><FiClock /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.nearSLA}</StatValue>
                  <StatLabel>Near SLA (24 hrs)</StatLabel>
                </div>
                <StatIcon><FiAlertCircle /></StatIcon>
              </StatHeader>
            </StatCard>
            <StatCard>
              <StatHeader>
                <div>
                  <StatValue>{stats.highRisk}</StatValue>
                  <StatLabel>High Risk</StatLabel>
                </div>
                <StatIcon><FiBarChart2 /></StatIcon>
              </StatHeader>
            </StatCard>
          </StatsGrid>
        </TopSection>

        <SectionHeader>
          <SectionTitle>Your Priority Queue for Today</SectionTitle>
          <ViewControls>
            <ViewButton active={viewMode === 'list'} onClick={() => setViewMode('list')}>
              <FiList /> List View
            </ViewButton>
            <ViewButton active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
              <FiGrid /> Grid View
            </ViewButton>
          </ViewControls>
        </SectionHeader>

        {viewMode === 'grid' ? (
          <KanbanContainer>
            <KanbanColumn>
              <ColumnHeader>
                <ColumnTitle>
                  <StatusDot color="#ef4444" />
                  Critical Tasks
                </ColumnTitle>
                <ColumnDescription>
                  Requires immediate action to prevent SLA breach or escalation
                </ColumnDescription>
                <SortControls>
                  <SortButton active={activeSort.critical === 'SLA Due'} onClick={() => setActiveSort({...activeSort, critical: 'SLA Due'})}>
                    SLA Due
                  </SortButton>
                  <SortButton active={activeSort.critical === 'High Priority'} onClick={() => setActiveSort({...activeSort, critical: 'High Priority'})}>
                    High Priority
                  </SortButton>
                  <SortButton>+1</SortButton>
                </SortControls>
              </ColumnHeader>
              <TaskList>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.critical.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No critical tasks</div>
                ) : (
                  <>
                    {tasks.critical.map(task => renderTask(task, '#ef4444'))}
                    {pagination.critical.hasMore && (
                      <div ref={criticalLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.critical.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </TaskList>
            </KanbanColumn>

            <KanbanColumn>
              <ColumnHeader>
                <ColumnTitle>
                  <StatusDot color="#f59e0b" />
                  Need Attention
                </ColumnTitle>
                <ColumnDescription>
                  Review required — may impact decision or delay processing
                </ColumnDescription>
                <SortControls>
                  <SortButton active={activeSort.attention === 'SLA Due'} onClick={() => setActiveSort({...activeSort, attention: 'SLA Due'})}>
                    SLA Due
                  </SortButton>
                  <SortButton active={activeSort.attention === 'High Priority'} onClick={() => setActiveSort({...activeSort, attention: 'High Priority'})}>
                    High Priority
                  </SortButton>
                </SortControls>
              </ColumnHeader>
              <TaskList>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.attention.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No tasks need attention</div>
                ) : (
                  <>
                    {tasks.attention.map(task => renderTask(task, '#f59e0b'))}
                    {pagination.attention.hasMore && (
                      <div ref={attentionLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.attention.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </TaskList>
            </KanbanColumn>

            <KanbanColumn>
              <ColumnHeader>
                <ColumnTitle>
                  <StatusDot color="#3b82f6" />
                  Pending Tasks
                </ColumnTitle>
                <ColumnDescription>
                  Actionable after critical and attention items are cleared
                </ColumnDescription>
                <SortControls>
                  <SortButton active={activeSort.pending === 'Quick to Close'} onClick={() => setActiveSort({...activeSort, pending: 'Quick to Close'})}>
                    Quick to Close
                  </SortButton>
                  <SortButton active={activeSort.pending === 'High Value'} onClick={() => setActiveSort({...activeSort, pending: 'High Value'})}>
                    High Value
                  </SortButton>
                </SortControls>
              </ColumnHeader>
              <TaskList>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.pending.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No pending tasks</div>
                ) : (
                  <>
                    {tasks.pending.map(task => renderTask(task, '#3b82f6'))}
                    {pagination.pending.hasMore && (
                      <div ref={pendingLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.pending.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </TaskList>
            </KanbanColumn>
          </KanbanContainer>
        ) : (
          <ListView>
            <ListSection>
              <ListSectionHeader>
                <StatusDot color="#ef4444" />
                <ListSectionTitle>Critical Tasks</ListSectionTitle>
              </ListSectionHeader>
              <ListTasksContainer>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.critical.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No critical tasks</div>
                ) : (
                  <>
                    {tasks.critical.map(task => renderTask(task, '#ef4444'))}
                    {pagination.critical.hasMore && (
                      <div ref={criticalLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.critical.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </ListTasksContainer>
            </ListSection>

            <ListSection>
              <ListSectionHeader>
                <StatusDot color="#f59e0b" />
                <ListSectionTitle>Need Attention</ListSectionTitle>
              </ListSectionHeader>
              <ListTasksContainer>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.attention.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No tasks need attention</div>
                ) : (
                  <>
                    {tasks.attention.map(task => renderTask(task, '#f59e0b'))}
                    {pagination.attention.hasMore && (
                      <div ref={attentionLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.attention.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </ListTasksContainer>
            </ListSection>

            <ListSection>
              <ListSectionHeader>
                <StatusDot color="#3b82f6" />
                <ListSectionTitle>Pending Tasks</ListSectionTitle>
              </ListSectionHeader>
              <ListTasksContainer>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading...</div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>{error}</div>
                ) : tasks.pending.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No pending tasks</div>
                ) : (
                  <>
                    {tasks.pending.map(task => renderTask(task, '#3b82f6'))}
                    {pagination.pending.hasMore && (
                      <div ref={pendingLoaderRef} style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                        {pagination.pending.loading ? 'Loading more...' : ''}
                      </div>
                    )}
                  </>
                )}
              </ListTasksContainer>
            </ListSection>
          </ListView>
        )}
      </MainContent>

      <DrawerOverlay isOpen={isAISidebarOpen} onClick={toggleSidebar} />
      
      <ResizeHandle onMouseDown={handleResizeStart} />

      <Sidebar width={sidebarWidth} isOpen={isAISidebarOpen}>
        <MetaClaimsAI
          userName={getUserData()?.firstname || 'User'}
          userEmail={getUserData()?.username || ''}
          initialHiddenQuery="Provide a detailed executive summary of my current workbench tasks.\nUse well-structured Markdown and include:\n\n- A short overview paragraph (what my workload looks like right now).\n\n- Key risks or SLA concerns (if any).\n- Notable high-value or high-risk tasks I should prioritize."
          chatContext="listing"
          contextMessage="Ask me to fetch data, explain rules, or help you start working on a claim."
          showProgress={true}
          progressValue="55%"
          progressLabel="Progress"
          suggestions={[
            "How is my workload compared to yesterday?",
            "Which claims have the highest financial impact?",
            "Show me high-risk cases in my queue.",
            "What is causing delays in my cases?"
          ]}
          onClose={() => setIsAISidebarOpen(false)}
        />
      </Sidebar>

      <FloatingActionButton 
        show={!isAISidebarOpen} 
        onClick={() => setIsAISidebarOpen(true)}
        title="Open MetaClaims AI"
      >
        <img src="/images/AI_Bubble.png" alt="AI"  />
      </FloatingActionButton>
    </Container>
  );
};

export default WorkbenchListing;

